import { StringMarshaller } from "@cervval.js/common-sql-core/src/marshallers/string.marshaller";
import { FieldMarshaller } from "@cervval.js/common-sql-core/src/sql.common";
import { assertIsNever, is } from "@cervval.js/common/src/tools/object.tools";

/** 
 * Enum for the French Profession et Catégories Socioprofessionnelles (or PCS for short)
 * which is a nomenclature to qualify the profession of citizens
 */
export enum PCS {
    FARMER = 1,
    LEADER = 2,
    EMPLOYEE = 3,
    MANAGER = 4,
    WORKER = 5,
    INTERMEDIARY = 6,
    RETIRED = 7,
    STUDENT = 8,
    OTHER_INACTIVE = 9
}

/** Assert that `v` is a value from enum `PCS` */
export function isPcsValue(v: unknown): v is PCS {
    if (v == null || typeof v !== "number") { return false; }
    const _v = v as PCS;
    switch (_v) {
        case PCS.FARMER:
        case PCS.LEADER:
        case PCS.EMPLOYEE:
        case PCS.MANAGER:
        case PCS.WORKER:
        case PCS.INTERMEDIARY:
        case PCS.RETIRED:
        case PCS.STUDENT:
        case PCS.OTHER_INACTIVE:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state monitored by ONAPS for access to adapted physical activity programs */
export enum ONAPS_STATE_OR_PATHOLOGIES {
    OVERWEIGHT = "overweight",
    HEART = "heart",
    DIABETES = "diabetes",
    CANCER = "cancer",
    NEUROLOGIC = "neurologic",
    PSYCHIATRY = "psychiatry",
    MUSCULOSKELETAL = "musculoskeletal",
    RESPIRATORY = "respiratory",
    INFECTIOUS = "infectious",
    KIDNEY = "kidney",
    INTESTINAL = "intestinal",
    FUNCTIONAL = "functional",
    TRANSPLANTED = "transplanted",
    OTHER = "other",
    AGE = "age",
    PREGNANCY = "pregnancy"
}

/** Assert that `v` is a value from enum `ONAPS_STATE_OR_PATHOLOGIES` */
export function isOnapsStateOrPathologiesValue(v: unknown): v is ONAPS_STATE_OR_PATHOLOGIES {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_STATE_OR_PATHOLOGIES;
    switch (_v) {
        case ONAPS_STATE_OR_PATHOLOGIES.OVERWEIGHT:
        case ONAPS_STATE_OR_PATHOLOGIES.HEART:
        case ONAPS_STATE_OR_PATHOLOGIES.DIABETES:
        case ONAPS_STATE_OR_PATHOLOGIES.CANCER:
        case ONAPS_STATE_OR_PATHOLOGIES.NEUROLOGIC:
        case ONAPS_STATE_OR_PATHOLOGIES.PSYCHIATRY:
        case ONAPS_STATE_OR_PATHOLOGIES.MUSCULOSKELETAL:
        case ONAPS_STATE_OR_PATHOLOGIES.RESPIRATORY:
        case ONAPS_STATE_OR_PATHOLOGIES.INFECTIOUS:
        case ONAPS_STATE_OR_PATHOLOGIES.KIDNEY:
        case ONAPS_STATE_OR_PATHOLOGIES.INTESTINAL:
        case ONAPS_STATE_OR_PATHOLOGIES.FUNCTIONAL:
        case ONAPS_STATE_OR_PATHOLOGIES.TRANSPLANTED:
        case ONAPS_STATE_OR_PATHOLOGIES.OTHER:
        case ONAPS_STATE_OR_PATHOLOGIES.AGE:
        case ONAPS_STATE_OR_PATHOLOGIES.PREGNANCY:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to overweight monitored by ONAPS */
export enum ONAPS_OVERWEIGHT {
    OBESITY = "obesity",
    OVERWEIGHT = "overweight",
    PRE_BARIATRIC = "pre-bariatric",
    POST_BARIATRIC = "post-bariatric",
    METABOLIC = "metabolic",
    STEATOSIS = "steatosis",
    OTHER = "other"
}

/** Assert that `v` is a value from enum `ONAPS_OVERWEIGHT` */
export function isOnapsOverweightValue(v: unknown): v is ONAPS_OVERWEIGHT {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_OVERWEIGHT;
    switch (_v) {
        case ONAPS_OVERWEIGHT.OBESITY:
        case ONAPS_OVERWEIGHT.OVERWEIGHT:
        case ONAPS_OVERWEIGHT.PRE_BARIATRIC:
        case ONAPS_OVERWEIGHT.POST_BARIATRIC:
        case ONAPS_OVERWEIGHT.METABOLIC:
        case ONAPS_OVERWEIGHT.STEATOSIS:
        case ONAPS_OVERWEIGHT.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to heart monitored by ONAPS */
export enum ONAPS_HEART {
    HYPERTENSION = "hypertension",
    CORONARY = "coronary",
    HEART_FAILURE = "heart_failure",
    RHYTHM_TROUBLE = "rhythm_trouble",
    CONGENITAL = "congenital",
    VALVULAR = "valvular",
    PULMONARY_EMBOLISM = "pulmonary_embolism",
    OTHER = "other"
}

/** Assert that `v` is a value from enum `ONAPS_HEART` */
export function isOnapsHeartValue(v: unknown): v is ONAPS_HEART {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_HEART;
    switch (_v) {
        case ONAPS_HEART.HYPERTENSION:
        case ONAPS_HEART.CORONARY:
        case ONAPS_HEART.HEART_FAILURE:
        case ONAPS_HEART.RHYTHM_TROUBLE:
        case ONAPS_HEART.CONGENITAL:
        case ONAPS_HEART.VALVULAR:
        case ONAPS_HEART.PULMONARY_EMBOLISM:
        case ONAPS_HEART.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to diabetes monitored by ONAPS */
export enum ONAPS_DIABETES {
    TYPE_2_UNTREATED = "type_2_untreated",
    TYPE_2_TREATED = "type_2_treated",
    TYPE_2_COMPLICATIONS = "type_2_complications",
    TYPE_1 = "type_1",
    TYPE_1_COMPLICATIONS = "type_1_complications",
    GESTATIONAL = "gestational"
}
/** Assert that `v` is a value from enum `ONAPS_DIABETES` */
export function isOnapsDiabetesValue(v: unknown): v is ONAPS_DIABETES {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_DIABETES;
    switch (_v) {
        case ONAPS_DIABETES.TYPE_2_UNTREATED:
        case ONAPS_DIABETES.TYPE_2_TREATED:
        case ONAPS_DIABETES.TYPE_2_COMPLICATIONS:
        case ONAPS_DIABETES.TYPE_1:
        case ONAPS_DIABETES.TYPE_1_COMPLICATIONS:
        case ONAPS_DIABETES.GESTATIONAL:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to cancer monitored by ONAPS */
export enum ONAPS_CANCER {
    BREAST = "breast",
    DIGESTIVE = "digestive",
    UTERUS = "uterus",
    LUNG = "lung",
    PROSTATE = "prostate",
    KIDNEY = "kidney",
    PANCREAS = "pancreas",
    CEREBRAL = "cerebral",
    OTHER = "other"
}
/** Assert that `v` is a value from enum `ONAPS_CANCER` */
export function isOnapsCancerValue(v: unknown): v is ONAPS_CANCER {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_CANCER;
    switch (_v) {
        case ONAPS_CANCER.BREAST:
        case ONAPS_CANCER.DIGESTIVE:
        case ONAPS_CANCER.UTERUS:
        case ONAPS_CANCER.LUNG:
        case ONAPS_CANCER.PROSTATE:
        case ONAPS_CANCER.KIDNEY:
        case ONAPS_CANCER.PANCREAS:
        case ONAPS_CANCER.CEREBRAL:
        case ONAPS_CANCER.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to neurology monitored by ONAPS */
export enum ONAPS_NEUROLOGIC {
    STROKE = "stroke",
    ALZHEIMER = "alzheimer",
    SCLEROSIS = "sclerosis",
    PARKINSON = "parkinson",
    MYOPATHY = "myopathy",
    OTHER = "other"
}
/** Assert that `v` is a value from enum `ONAPS_NEUROLOGIC` */
export function isOnapsNeurologicValue(v: unknown): v is ONAPS_NEUROLOGIC {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_NEUROLOGIC;
    switch (_v) {
        case ONAPS_NEUROLOGIC.STROKE:
        case ONAPS_NEUROLOGIC.ALZHEIMER:
        case ONAPS_NEUROLOGIC.SCLEROSIS:
        case ONAPS_NEUROLOGIC.PARKINSON:
        case ONAPS_NEUROLOGIC.MYOPATHY:
        case ONAPS_NEUROLOGIC.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to psychiatry monitored by ONAPS */
export enum ONAPS_PSYCHIATRY {
    DEPRESSION = "depression",
    ANXIETY = "anxiety",
    BURN_OUT = "burn-out",
    SCHIZOPHRENIA = "schizophrenia",
    OTHER = "other"
}
/** Assert that `v` is a value from enum `ONAPS_PSYCHIATRY` */
export function isOnapsPsychiatryValue(v: unknown): v is ONAPS_PSYCHIATRY {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_PSYCHIATRY;
    switch (_v) {
        case ONAPS_PSYCHIATRY.DEPRESSION:
        case ONAPS_PSYCHIATRY.ANXIETY:
        case ONAPS_PSYCHIATRY.BURN_OUT:
        case ONAPS_PSYCHIATRY.SCHIZOPHRENIA:
        case ONAPS_PSYCHIATRY.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to skeletons and muscles monitored by ONAPS */
export enum ONAPS_MUSCULOSKELETAL {
    LOW_BACK_PAIN = "low-back-pain",
    OSTEOARTHRITIS = "osteoarthritis",
    RHEUMATOID_ARTHRITIS = "rheumatoid_arthritis",
    ANKYLOSING_SPONDYLITIS = "ankylosing_spondylitis",
    INFLAMMATORY_RHEUMATISM = "inflammatory_rheumatism",
    OTHER = "other"
}
/** Assert that `v` is a value from enum `ONAPS_MUSCULOSKELETAL` */
export function isOnapsMusculoskeletalValue(v: unknown): v is ONAPS_MUSCULOSKELETAL {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_MUSCULOSKELETAL;
    switch (_v) {
        case ONAPS_MUSCULOSKELETAL.LOW_BACK_PAIN:
        case ONAPS_MUSCULOSKELETAL.OSTEOARTHRITIS:
        case ONAPS_MUSCULOSKELETAL.RHEUMATOID_ARTHRITIS:
        case ONAPS_MUSCULOSKELETAL.ANKYLOSING_SPONDYLITIS:
        case ONAPS_MUSCULOSKELETAL.INFLAMMATORY_RHEUMATISM:
        case ONAPS_MUSCULOSKELETAL.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to respiration monitored by ONAPS */
export enum ONAPS_RESPIRATORY {
    CHRONIC_OBSTRUCTIVE_PULMONARY_DISEASE = "chronic_obstructive_pulmonary_disease",
    ASTHMA = "asthma",
    RESPIRATORY_FAILURE = "respiratory_failure",
    SLEEP_APNEA = "sleep_apnea",
    OTHER = "other"
}
/** Assert that `v` is a value from enum `ONAPS_RESPIRATORY` */
export function isOnapsRespiratoryValue(v: unknown): v is ONAPS_RESPIRATORY {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_RESPIRATORY;
    switch (_v) {
        case ONAPS_RESPIRATORY.CHRONIC_OBSTRUCTIVE_PULMONARY_DISEASE:
        case ONAPS_RESPIRATORY.ASTHMA:
        case ONAPS_RESPIRATORY.RESPIRATORY_FAILURE:
        case ONAPS_RESPIRATORY.SLEEP_APNEA:
        case ONAPS_RESPIRATORY.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to infection monitored by ONAPS */
export enum ONAPS_INFECTIOUS {
    HIV = "hiv",
    COVID = "covid",
    OTHER = "other"
}
/** Assert that `v` is a value from enum `ONAPS_INFECTIOUS` */
export function isOnapsInfectiousValue(v: unknown): v is ONAPS_INFECTIOUS {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_INFECTIOUS;
    switch (_v) {
        case ONAPS_INFECTIOUS.HIV:
        case ONAPS_INFECTIOUS.COVID:
        case ONAPS_INFECTIOUS.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to kidney monitored by ONAPS */
export enum ONAPS_KIDNEY {
    KIDNEY_FAILURE_DIALYSED = "kidney_failure_dialysed",
    KIDNEY_FAILURE_WITHOUT_DIALYSIS = "kidney_failure_without_dialysis",
    OTHER = "other"
}

/** Assert that `v` is a value from enum `ONAPS_KIDNEY` */
export function isOnapsKidneyValue(v: unknown): v is ONAPS_KIDNEY {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_KIDNEY;
    switch (_v) {
        case ONAPS_KIDNEY.KIDNEY_FAILURE_DIALYSED:
        case ONAPS_KIDNEY.KIDNEY_FAILURE_WITHOUT_DIALYSIS:
        case ONAPS_KIDNEY.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to the intestinal system monitored by ONAPS */
export enum ONAPS_INTESTINAL {
    CROHN = "crohn",
    ULCERATIVE_COLITIS = "ulcerative_colitis",
    OTHER = "other"
}

/** Assert that `v` is a value from enum `ONAPS_INTESTINAL` */
export function isOnapsIntestinalValue(v: unknown): v is ONAPS_INTESTINAL {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_INTESTINAL;
    switch (_v) {
        case ONAPS_INTESTINAL.CROHN:
        case ONAPS_INTESTINAL.ULCERATIVE_COLITIS:
        case ONAPS_INTESTINAL.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to functional symptoms monitored by ONAPS */
export enum ONAPS_FUNCTIONAL {
    FIBROMYALGIA = "fibromyalgia",
    CHRONIC_PAIN = "chronic_pain",
    OTHER = "other"
}

/** Assert that `v` is a value from enum `ONAPS_FUNCTIONAL` */
export function isOnapsFunctionalValue(v: unknown): v is ONAPS_FUNCTIONAL {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_FUNCTIONAL;
    switch (_v) {
        case ONAPS_FUNCTIONAL.FIBROMYALGIA:
        case ONAPS_FUNCTIONAL.CHRONIC_PAIN:
        case ONAPS_FUNCTIONAL.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to organ transplantation monitored by ONAPS */
export enum ONAPS_TRANSPLANTED {
    KIDNEY = "kidney",
    HEART = "heart",
    LIVER = "liver",
    OTHER = "other"
}

/** Assert that `v` is a value from enum `ONAPS_TRANSPLANTED` */
export function isOnapsTransplantedValue(v: unknown): v is ONAPS_TRANSPLANTED {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_TRANSPLANTED;
    switch (_v) {
        case ONAPS_TRANSPLANTED.KIDNEY:
        case ONAPS_TRANSPLANTED.HEART:
        case ONAPS_TRANSPLANTED.LIVER:
        case ONAPS_TRANSPLANTED.OTHER:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to pregnancy monitored by ONAPS */
export enum ONAPS_PREGNANCY {
    PRE_PREGNANCY = "pre-pregnancy",
    POST_PREGNANCY = "post-pregnancy"
}

/** Assert that `v` is a value from enum `ONAPS_PREGNANCY` */
export function isOnapsPregnancyValue(v: unknown): v is ONAPS_PREGNANCY {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_PREGNANCY;
    switch (_v) {
        case ONAPS_PREGNANCY.PRE_PREGNANCY:
        case ONAPS_PREGNANCY.POST_PREGNANCY:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

/** Enum for pathologies or state related to age monitored by ONAPS */
export enum ONAPS_AGE {
    FALL_PREVENTION = "fall_prevention",
    SARCOPENIA = "sarcopenia"
}

/** Assert that `v` is a value from enum `ONAPS_AGE` */
export function isOnapsAgeValue(v: unknown): v is ONAPS_AGE {
    if (v == null || typeof v !== "string") { return false; }
    const _v = v as ONAPS_AGE;
    switch (_v) {
        case ONAPS_AGE.FALL_PREVENTION:
        case ONAPS_AGE.SARCOPENIA:
            return true;
        default:
            assertIsNever(_v);
            return false;
    }
}

export type ALL_ONAPS_STATE_OR_PATHOLOGIES = ONAPS_AGE | ONAPS_PREGNANCY | ONAPS_TRANSPLANTED | ONAPS_FUNCTIONAL | ONAPS_INTESTINAL |
    ONAPS_KIDNEY | ONAPS_INFECTIOUS | ONAPS_RESPIRATORY | ONAPS_MUSCULOSKELETAL | ONAPS_PSYCHIATRY | ONAPS_NEUROLOGIC | ONAPS_CANCER |
    ONAPS_DIABETES | ONAPS_HEART | ONAPS_OVERWEIGHT;

/** Scale used inside a Garnier form, consisting of integer values between 1 and 10, 1 being the worst value and 10 the best value. Enum is formatted for CSV export of ONAPS data */
export enum GARNIER_SCALE {
    _1 = "1",
    _2 = "2",
    _3 = "3",
    _4 = "4",
    _5 = "5",
    _6 = "6",
    _7 = "7",
    _8 = "8",
    _9 = "9",
    _10 = "10"
}

/** Enum for the conditions of the sport the patient is practicing with a MSS structure. Enum is formatted for CSV export of ONAPS data*/
export enum SPORT_ACTIVITY_MODE {
    APA_PROGRAM = 1,
    ASSOCIATION = 2,
    FREE_PRACTICE = 3,
    PRACTICE_WITH_PROFESSIONAL = 4
}

export type OnapsFieldTypes = {
    "PCS": PCS,
    "ONAPS_STATE_OR_PATHOLOGIES": ONAPS_STATE_OR_PATHOLOGIES,
    "GARNIER_SCALE": GARNIER_SCALE,
    "SPORT_ACTIVITY_PROGRAM": SPORT_ACTIVITY_MODE
}

// FieldTypeNames are used in SQL table definitions in order to define which marshallers should be used to marshal / unmarshal object from / to DB
export type OnapsFieldTypeName = keyof OnapsFieldTypes;

/** Set mandatory marshallers for `OnapsFieldTypes` into given marshaller map */
export function setMarshallersForOnapsFieldTypes<FieldTypes extends OnapsFieldTypes>(marshallers: Map<keyof FieldTypes, FieldMarshaller<FieldTypes[keyof FieldTypes]> | string>): void {
    marshallers.set("ONAPS_STATE_OR_PATHOLOGIES", is<FieldMarshaller<FieldTypes["ONAPS_STATE_OR_PATHOLOGIES"]>>(new StringMarshaller({ maxLength: 50 })));
    marshallers.set("GARNIER_SCALE", is<FieldMarshaller<FieldTypes["GARNIER_SCALE"]>>(new StringMarshaller({ maxLength: 2 })));
}